import type { Theme, SxProps } from '@mui/material/styles';

import React from 'react';

import {
  Box,
  Card,
  Chip,
  Stack,
  Button,
  Typography,
  LinearProgress,
  CircularProgress,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/hook/hooks';
import { setActiveTab } from 'src/pages/setting/components/billing/slice/billingSlice';

import { Iconify } from 'src/components/iconify';

import { useGetSmsUsageQuery } from '../../../slice/channelApi';

export interface SmsCreditWidgetProps {
  compact?: boolean;
  showBuyButton?: boolean;
  onBuyBundle?: () => void;
  sx?: SxProps<Theme>;
}

export function SmsCreditWidget({
  compact = false,
  showBuyButton = true,
  onBuyBundle,
  sx,
}: SmsCreditWidgetProps) {
  const { t } = useTranslate('translation');
  const router = useRouter();
  const dispatch = useAppDispatch();

  const { data: usageRes, isLoading, isError, refetch } = useGetSmsUsageQuery();

  const tr = (key: string, fallback: string) => {
    const val = t(key);
    return !val || val === key ? fallback : val;
  };

  const handleBuyBundle = () => {
    dispatch(setActiveTab('addons'));
    if (onBuyBundle) {
      onBuyBundle();
    } else {
      router.push(`${paths.setting.billing.root}?tab=addons`);
    }
  };

  if (isLoading) {
    return (
      <Card
        variant="outlined"
        sx={{
          p: compact ? 2 : 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: compact ? 80 : 120,
          borderColor: 'divider',
          ...sx,
        }}
      >
        <CircularProgress size={24} />
      </Card>
    );
  }

  if (isError || !usageRes?.success || !usageRes?.data) {
    return (
      <Card
        variant="outlined"
        sx={{
          p: compact ? 2 : 3,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          ...sx,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="error">
            {tr('FAILED_TO_LOAD_SMS_CREDITS', 'Failed to load SMS credits')}
          </Typography>
          <Button size="small" variant="outlined" onClick={() => refetch()}>
            {tr('RETRY', 'Retry')}
          </Button>
        </Stack>
      </Card>
    );
  }

  const { value = 0, used = 0, remaining = 0, hasLimit = false } = usageRes.data;

  // Calculate percentage used
  const percentUsed = value > 0 ? Math.min(100, Math.max(0, Math.round((used / value) * 100))) : 0;
  const isOutOfCredits = remaining <= 0 && hasLimit;
  const isNoBundle = !hasLimit;
  const isLowCredits = remaining > 0 && remaining <= 1000;

  // Resolve status chip
  let chipColor: 'success' | 'warning' | 'error' | 'default' = 'success';
  let chipLabel = tr('SMS_STATUS_ACTIVE', 'Active');

  if (isNoBundle) {
    chipColor = 'warning';
    chipLabel = tr('SMS_NO_BUNDLE_YET', 'No bundle yet');
  } else if (isOutOfCredits) {
    chipColor = 'error';
    chipLabel = tr('SMS_OUT_OF_CREDITS', 'Out of credits');
  } else if (isLowCredits) {
    chipColor = 'warning';
    chipLabel = tr('SMS_LOW_CREDITS', 'Low credits');
  }

  if (compact) {
    return (
      <Card
        variant="outlined"
        sx={{
          p: 2,
          borderColor: (theme) =>
            isOutOfCredits || isNoBundle
              ? theme.palette.error.main
              : isLowCredits
                ? theme.palette.warning.main
                : 'divider',
          bgcolor: 'background.paper',
          ...sx,
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: (theme) =>
                    isOutOfCredits || isNoBundle
                      ? 'error.soft'
                      : isLowCredits
                        ? 'warning.soft'
                        : 'primary.soft',
                  color: (theme) =>
                    isOutOfCredits || isNoBundle
                      ? 'error.main'
                      : isLowCredits
                        ? 'warning.main'
                        : 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify icon="solar:chat-round-money-bold" width={20} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                  {tr('SMS_CREDITS_REMAINING', 'Remaining SMS Credits')}
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {remaining.toLocaleString()}
                </Typography>
              </Box>
            </Stack>

            <Chip
              size="small"
              color={chipColor}
              variant="soft"
              label={chipLabel}
              sx={{ fontWeight: 700, height: 24 }}
            />
          </Stack>

          {hasLimit && (
            <Box>
              <LinearProgress
                variant="determinate"
                value={percentUsed}
                color={isOutOfCredits ? 'error' : isLowCredits ? 'warning' : 'primary'}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Stack direction="row" justifyContent="space-between" mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {used.toLocaleString()} {tr('USED', 'used')}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {value.toLocaleString()} {tr('TOTAL', 'total')}
                </Typography>
              </Stack>
            </Box>
          )}

          {showBuyButton && (isOutOfCredits || isNoBundle || isLowCredits) && (
            <Button
              size="small"
              variant={isOutOfCredits || isNoBundle ? 'contained' : 'outlined'}
              color={isOutOfCredits || isNoBundle ? 'error' : 'warning'}
              onClick={handleBuyBuynderAction}
              startIcon={<Iconify icon="solar:bag-check-bold" />}
              fullWidth
            >
              {tr('BUY_SMS_BUNDLE', 'Buy SMS Bundle')}
            </Button>
          )}
        </Stack>
      </Card>
    );
  }

  function handleBuyBuynderAction() {
    handleBuyBundle();
  }

  return (
    <Card
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 2,
        borderColor: (theme) =>
          isOutOfCredits || isNoBundle
            ? theme.palette.error.light
            : isLowCredits
              ? theme.palette.warning.light
              : 'divider',
        boxShadow: (theme) => theme.customShadows?.z8 || '0 4px 20px 0 rgba(0,0,0,0.05)',
        ...sx,
      }}
    >
      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: (theme) =>
                  isOutOfCredits || isNoBundle
                    ? 'error.main'
                    : isLowCredits
                      ? 'warning.main'
                      : 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              <Iconify icon="solar:chat-round-money-bold" width={28} />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {tr('SMS_CREDIT_BALANCE', 'SMS Credit Balance')}
              </Typography>
              <Typography variant="h3" fontWeight={800} color="text.primary">
                {remaining.toLocaleString()}
                <Typography
                  component="span"
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {tr('CREDITS', 'credits')}
                </Typography>
              </Typography>
            </Box>
          </Stack>

          <Stack alignItems="flex-end" spacing={1}>
            <Chip
              color={chipColor}
              variant="soft"
              label={chipLabel}
              sx={{ fontWeight: 700, px: 1 }}
            />
            {showBuyButton && (
              <Button
                size="small"
                variant={isOutOfCredits || isNoBundle ? 'contained' : 'outlined'}
                color={isOutOfCredits || isNoBundle ? 'error' : 'primary'}
                onClick={handleBuyBundle}
                startIcon={<Iconify icon="solar:bag-check-bold" />}
              >
                {tr('BUY_BUNDLE', 'Buy Bundle')}
              </Button>
            )}
          </Stack>
        </Stack>

        {hasLimit ? (
          <Box>
            <Stack direction="row" justifyContent="space-between" mb={0.75}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {tr('USAGE_PROGRESS', 'Usage Progress')} ({percentUsed}%)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {used.toLocaleString()} / {value.toLocaleString()}{' '}
                {tr('SEGMENTS_USED', 'segments used')}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={percentUsed}
              color={isOutOfCredits ? 'error' : isLowCredits ? 'warning' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,193,7,0.1)' : '#fff8e1',
              border: '1px solid',
              borderColor: 'warning.light',
            }}
          >
            <Typography variant="body2" color="warning.dark" fontWeight={500}>
              💡{' '}
              {tr(
                'NO_BUNDLE_MSG',
                'You have not purchased an SMS bundle yet. Sending is blocked until a bundle is active.'
              )}
            </Typography>
          </Box>
        )}
      </Stack>
    </Card>
  );
}

export default SmsCreditWidget;
